import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LZString from "lz-string";
import { useDesign } from "./DesignContext";
import BaseCard from "./BaseCard";
import {
  trackMemoCardPageView,
  trackPreviewReached,
  trackCardShared,
} from "./MemoCardAnalytics";

const Preview = () => {
  const navigate = useNavigate();
  const { selectedDesign, designs, answers, name } = useDesign();

  useEffect(() => {
    trackMemoCardPageView("preview");
    trackPreviewReached(Object.keys(answers).length);
  }, [answers]);

  // Default design for testing
  const defaultDesign = {
    id: 1,
    title: "Classic Floral",
    image: `${process.env.PUBLIC_URL}/assets/memo-cards/mothers-day/bg1.svg`,
  };

  // Default answers for testing
  const defaultAnswers = {
    0: "Remember that time we baked cookies together and made a huge mess? The kitchen was covered in flour, but we laughed so hard! 🍪",
    1: "You always say 'Everything happens for a reason' and it's helped me through so many tough times 💫",
    2: "I love our Sunday morning walks in the park, especially when we stop to feed the ducks 🦆",
    3: "I'm so proud of how you started your own business and never gave up on your dreams 💼",
    4: "That time we tried to assemble the new bookshelf and everything went wrong! We ended up with extra pieces and a lopsided shelf 😂",
    5: "Your endless patience and ability to stay calm in any situation is truly amazing 🕊️",
    6: "I admire how you always find the positive side of things, even in difficult situations ✨",
    7: "I'm grateful I inherited your love for cooking and trying new recipes 👩‍🍳",
    8: "I'll never forget how happy you were when you got your dream job offer! Your smile lit up the whole room 💫",
    9: "You taught me to always stand up for what's right, even when it's not the easy thing to do 💪",
    10: "I wish I knew earlier how much you love stargazing and astronomy. Now we can share that passion together 🌟",
    11: "I hope we can travel to Japan together one day and experience the cherry blossoms in spring 🌸",
  };

  const selectedDesignData =
    designs.find((d) => d.id === selectedDesign) || defaultDesign;
  const testAnswers =
    Object.keys(answers).length === 0 ? defaultAnswers : answers;

  const cardData = {
    design: selectedDesignData,
    answers: testAnswers,
    name,
  };

  const handleCopyLink = () => {
    const compressed = LZString.compressToEncodedURIComponent(
      JSON.stringify(cardData)
    );
    const shareUrl = `${window.location.origin}/memo-card/shared?data=${compressed}`;
    navigator.clipboard.writeText(shareUrl);
    trackCardShared("copy_link");
  };

  const handleShare = async () => {
    if (navigator.share) {
      const compressed = LZString.compressToEncodedURIComponent(
        JSON.stringify(cardData)
      );
      const shareUrl = `${window.location.origin}/memo-card/shared?data=${compressed}`;
      try {
        await navigator.share({
          title: "Mother's Day Memory Card",
          text: "Check out this special Mother's Day memory card I made!",
          url: shareUrl,
        });
        trackCardShared("native");
      } catch (error) {
        console.error("Error sharing:", error);
      }
    }
  };

  if (!selectedDesignData) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f8f6ed",
          p: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h5" color="text.secondary">
          Please select a design first
        </Typography>
      </Box>
    );
  }

  return (
    <BaseCard
      cardData={cardData}
      name={name}
      showShareButtons={true}
      onShare={handleShare}
      onCopyLink={handleCopyLink}
      showSparkles={true}
    />
  );
};

export default Preview;
